import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../context/TranslationContext';
import { Navbar, Footer } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Image, Upload, Heart, Share2, Copy, Check,
  DollarSign, Users, Trophy, Link as LinkIcon
} from 'lucide-react';

import { safeArray } from '../lib/utils';

import { getApiBaseUrl } from '@/lib/apiBase';
const API_URL = getApiBaseUrl();

// Student Projects Page
export const ProjectsPage = () => {
  const { user, token } = useAuth();
  const { t, tFormat } = useTranslation();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [myProjects, setMyProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    title: '', description: '', images: []
  });

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const [publicRes, myRes] = await Promise.all([
        axios.get(`${API_URL}/projects`),
        token ? axios.get(`${API_URL}/projects/my`, { headers }) : Promise.resolve({ data: [] })
      ]);
      setProjects(safeArray(publicRes.data));
      setMyProjects(safeArray(myRes.data));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token) {
      navigate('/login');
      return;
    }
    
    setSubmitting(true);
    const data = new FormData();
    data.append('title', formData.title);
    data.append('description', formData.description);
    formData.images.forEach((file) => {
      data.append('images', file);
    });

    try {
      await axios.post(`${API_URL}/projects`, data, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      toast.success(t('proj_toast_submitted', 'Project submitted for review!'));
      setShowForm(false);
      setFormData({ title: '', description: '', images: [] });
      fetchProjects();
    } catch (err) {
      toast.error(t('proj_toast_submit_fail', 'Failed to submit project'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleLike = async (projectId) => {
    if (!token) {
      navigate('/login');
      return;
    }
    try {
      await axios.post(`${API_URL}/projects/${projectId}/like`, {}, { headers });
      fetchProjects();
    } catch (err) {
      toast.error(t('proj_toast_like_fail', 'Failed to like project'));
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files).slice(0, 5);
    setFormData({ ...formData, images: files });
  };

  return (
    <div className="min-h-screen bg-background" data-testid="projects-page">
      <Navbar />
      
      <main className="pt-20 pb-12">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Header */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
            <div>
              <h1 className="text-4xl font-serif font-medium mb-2">
                {t('proj_page_title', 'Community Projects')}
              </h1>
              <p className="text-muted-foreground">
                {t('proj_page_subtitle', 'Showcase your work and get inspired by others')}
              </p>
            </div>
            <Button onClick={() => setShowForm(!showForm)} data-testid="submit-project-btn">
              <Upload className="w-4 h-4 mr-2" />
              {t('proj_submit_btn', 'Submit Your Project')}
            </Button>
          </div>

          {/* Submit Form */}
          {showForm && (
            <Card className="mb-8">
              <CardHeader>
                <CardTitle>{t('proj_form_title', 'Share Your Project')}</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label>{t('proj_field_title', 'Project Title')}</Label>
                    <Input 
                      value={formData.title}
                      onChange={(e) => setFormData({...formData, title: e.target.value})}
                      placeholder={t('proj_title_ph', 'My Awesome Cabinet Design')}
                      required
                      data-testid="project-title-input"
                    />
                  </div>
                  <div>
                    <Label>{t('proj_field_desc', 'Description')}</Label>
                    <Textarea 
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      placeholder={t('proj_desc_ph', 'Tell us about your project...')}
                      required
                      data-testid="project-desc-input"
                    />
                  </div>
                  <div>
                    <Label>{t('proj_field_images', 'Images (max 5)')}</Label>
                    <Input 
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleFileChange}
                      data-testid="project-images-input"
                    />
                    {formData.images.length > 0 && (
                      <p className="text-sm text-muted-foreground mt-1">
                        {tFormat(
                          'proj_files_selected_fmt',
                          { n: formData.images.length },
                          '{n} file(s) selected'
                        )}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button type="submit" disabled={submitting} data-testid="submit-project-form-btn">
                      {submitting
                        ? t('proj_submitting', 'Submitting...')
                        : t('proj_submit', 'Submit Project')}
                    </Button>
                    <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                      {t('proj_cancel', 'Cancel')}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          {/* Tabs */}
          <Tabs defaultValue="community">
            <TabsList>
              <TabsTrigger value="community">
                {tFormat('proj_tab_community_fmt', { n: projects.length }, 'Community ({n})')}
              </TabsTrigger>
              {user && (
                <TabsTrigger value="my">
                  {tFormat('proj_tab_my_fmt', { n: myProjects.length }, 'My Projects ({n})')}
                </TabsTrigger>
              )}
            </TabsList>

            <TabsContent value="community" className="mt-6">
              {projects.length === 0 ? (
                <Card>
                  <CardContent className="p-12 text-center">
                    <Image className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                    <h3 className="text-lg font-semibold mb-2">
                      {t('proj_empty_title', 'No projects yet')}
                    </h3>
                    <p className="text-muted-foreground">
                      {t('proj_empty_hint', 'Be the first to share your work!')}
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {projects.map((project) => (
                    <Card key={project.id} className="overflow-hidden group">
                      {project.images?.length > 0 && (
                        <img 
                          src={`${process.env.REACT_APP_BACKEND_URL}${project.images[0]}`}
                          alt={project.title}
                          className="w-full aspect-video object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      )}
                      <CardContent className="p-4">
                        <h3 className="font-semibold mb-1">{project.title}</h3>
                        <p className="text-sm text-muted-foreground mb-2">
                          {tFormat('proj_by', { name: project.user_name }, 'by {name}')}
                        </p>
                        <p className="text-sm line-clamp-2 mb-4">{project.description}</p>
                        <div className="flex items-center justify-between">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleLike(project.id)}
                            data-testid={`like-project-${project.id}`}
                          >
                            <Heart className="w-4 h-4 mr-1" />
                            {project.likes || 0}
                          </Button>
                          <span className="text-xs text-muted-foreground">
                            {new Date(project.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {user && (
              <TabsContent value="my" className="mt-6">
                {myProjects.length === 0 ? (
                  <Card>
                    <CardContent className="p-12 text-center">
                      <Upload className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                      <h3 className="text-lg font-semibold mb-2">
                        {t('proj_empty_my_title', 'No projects submitted')}
                      </h3>
                      <p className="text-muted-foreground">
                        {t(
                          'proj_empty_my_hint',
                          'Share your first project with the community!'
                        )}
                      </p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {myProjects.map((project) => (
                      <Card key={project.id} className="overflow-hidden">
                        {project.images?.length > 0 && (
                          <img 
                            src={`${process.env.REACT_APP_BACKEND_URL}${project.images[0]}`}
                            alt={project.title}
                            className="w-full aspect-video object-cover"
                          />
                        )}
                        <CardContent className="p-4">
                          <Badge 
                            variant={
                              project.status === 'approved' ? 'default' :
                              project.status === 'rejected' ? 'destructive' : 'secondary'
                            }
                            className="mb-2 capitalize"
                          >
                            {project.status}
                          </Badge>
                          <h3 className="font-semibold mb-1">{project.title}</h3>
                          <p className="text-sm line-clamp-2">{project.description}</p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>
            )}
          </Tabs>
        </div>
      </main>

      <Footer />
    </div>
  );
};

// Affiliate Dashboard Page
export const AffiliatePage = () => {
  const { user, token } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [link, setLink] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      const [linkRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/affiliate/link`, { headers }),
        axios.get(`${API_URL}/affiliate/stats`, { headers })
      ]);
      setLink(linkRes.data);
      setStats(statsRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const copyLink = () => {
    const fullLink = `${window.location.origin}/signup?ref=${link?.code}`;
    navigator.clipboard.writeText(fullLink);
    setCopied(true);
    toast.success(t('aff_toast_copied', 'Referral link copied!'));
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <main className="pt-20 pb-12">
          <div className="max-w-4xl mx-auto px-6 py-12">
            <div className="animate-pulse space-y-6">
              <div className="h-12 bg-muted w-1/3 rounded" />
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[1, 2, 3].map(i => <div key={i} className="h-32 bg-muted rounded-xl" />)}
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="affiliate-page">
      <Navbar />
      
      <main className="pt-20 pb-12">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-12">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Share2 className="w-8 h-8 text-accent" />
            </div>
            <h1 className="text-4xl font-serif font-medium mb-2">
              {t('aff_title', 'Refer & Earn')}
            </h1>
            <p className="text-muted-foreground">
              {t(
                'aff_subtitle_detail',
                'Earn ₹500 for every friend who purchases premium access'
              )}
            </p>
          </div>

          {/* Referral Link */}
          <Card className="mb-8">
            <CardContent className="p-6">
              <Label className="mb-2 block">
                {t('aff_referral_link_label', 'Your Referral Link')}
              </Label>
              <div className="flex gap-2">
                <Input 
                  value={`${window.location.origin}/signup?ref=${link?.code || ''}`}
                  readOnly
                  className="font-mono text-sm"
                  data-testid="referral-link"
                />
                <Button
                  onClick={copyLink}
                  data-testid="copy-link-btn"
                  title={copied ? t('aff_copied', 'Copied!') : t('aff_copy_link', 'Copy Link')}
                  aria-label={copied ? t('aff_copied', 'Copied!') : t('aff_copy_link', 'Copy Link')}
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                {t(
                  'aff_share_blurb',
                  'Share this link with friends. When they sign up and purchase, you earn a commission!'
                )}
              </p>
            </CardContent>
          </Card>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-500/10 rounded-xl flex items-center justify-center">
                    <Users className="w-6 h-6 text-blue-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats?.total_referrals || 0}</p>
                    <p className="text-sm text-muted-foreground">
                      {t('aff_stat_total', 'Total Referrals')}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-green-500/10 rounded-xl flex items-center justify-center">
                    <Trophy className="w-6 h-6 text-green-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats?.successful_referrals || 0}</p>
                    <p className="text-sm text-muted-foreground">
                      {t('aff_stat_converted', 'Converted')}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-amber-500/10 rounded-xl flex items-center justify-center">
                    <DollarSign className="w-6 h-6 text-amber-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">₹{(stats?.total_earnings || 0).toLocaleString()}</p>
                    <p className="text-sm text-muted-foreground">
                      {t('aff_stat_total_earned', 'Total Earned')}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-purple-500/10 rounded-xl flex items-center justify-center">
                    <DollarSign className="w-6 h-6 text-purple-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">₹{(stats?.pending_earnings || 0).toLocaleString()}</p>
                    <p className="text-sm text-muted-foreground">
                      {t('aff_stat_pending', 'Pending Payout')}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* How it works */}
          <Card>
            <CardHeader>
              <CardTitle>{t('aff_how_card_title', 'How It Works')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-3">
                    <LinkIcon className="w-6 h-6 text-accent" />
                  </div>
                  <h3 className="font-semibold mb-1">
                    {t('aff_step1_title', '1. Share Your Link')}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {t('aff_step1_desc', 'Copy and share your unique referral link')}
                  </p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Users className="w-6 h-6 text-accent" />
                  </div>
                  <h3 className="font-semibold mb-1">
                    {t('aff_step2_title', '2. Friends Sign Up')}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {t('aff_step2_desc', 'They create an account using your link')}
                  </p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-3">
                    <DollarSign className="w-6 h-6 text-accent" />
                  </div>
                  <h3 className="font-semibold mb-1">
                    {t('aff_step3_title', '3. Earn Commission')}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {t('aff_step3_desc', 'Get 10% when they purchase premium')}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Referrals List */}
          {stats?.referrals?.length > 0 && (
            <Card className="mt-8">
              <CardHeader>
                <CardTitle>{t('aff_your_referrals', 'Your Referrals')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {stats.referrals.map((ref, i) => (
                    <div key={i} className="flex items-center justify-between py-2 border-b last:border-0">
                      <div>
                        <p className="font-medium">{ref.referred_email}</p>
                        <p className="text-sm text-muted-foreground">
                          {new Date(ref.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <Badge variant={ref.status === 'converted' ? 'default' : 'secondary'} className="capitalize">
                        {ref.status}
                        {ref.commission && ` (+₹${ref.commission})`}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};
